/**
 * GTM DataLayer Management Utilities
 * Handles safe dataLayer access and event pushing for GTM
 */

import type { DataLayerEvent } from './types';

/**
 * Check if dataLayer is available
 * Safe to call in any environment (client/server)
 */
export const isDataLayerAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!(window.dataLayer && Array.isArray(window.dataLayer));
};

/**
 * Get the dataLayer array
 * Initialize if it doesn't exist
 */
export const getDataLayer = (): DataLayerEvent[] => {
  if (typeof window === 'undefined') return [];

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  return window.dataLayer;
};

/**
 * Push event to GTM dataLayer
 * Core function for all tracking events
 * 
 * @param event - Event name (should not contain spaces)
 * @param data - Event data object
 * @returns true if push succeeded, false if dataLayer unavailable
 * 
 * @example
 * pushToDataLayer('purchase', {
 *   transaction_id: '123',
 *   value: 99.99,
 *   currency: 'INR'
 * });
 */
export const pushToDataLayer = (
  event: string,
  data: Record<string, any> = {}
): boolean => {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot push to dataLayer: window is undefined');
    return false;
  }

  const dataLayer = getDataLayer();

  const eventData: DataLayerEvent = {
    event,
    timestamp: new Date().toISOString(),
    ...data,
  };

  try {
    dataLayer.push(eventData);
    
    // Log in debug mode
    if (process.env.NEXT_PUBLIC_DEBUG_TRACKING === 'true') {
      console.log('[GTM] Event pushed:', eventData);
    }

    return true;
  } catch (error) {
    console.error('[GTM] Error pushing to dataLayer:', error);
    return false;
  }
};

/**
 * Clear dataLayer (useful for testing/debugging)
 * Use with caution in production
 */
export const clearDataLayer = (): void => {
  if (typeof window === 'undefined') return;

  const dataLayer = getDataLayer();
  dataLayer.length = 0;
  
  if (process.env.NEXT_PUBLIC_DEBUG_TRACKING === 'true') {
    console.log('[GTM] DataLayer cleared');
  }
};

/**
 * Get last N events from dataLayer for debugging
 */
export const getLastDataLayerEvents = (count: number = 10): DataLayerEvent[] => {
  const dataLayer = getDataLayer();
  return dataLayer.slice(Math.max(0, dataLayer.length - count));
};

/**
 * Set GTM user properties (custom user variables)
 * Called after user login or profile update
 */
export const setUserProperties = (
  userId: string,
  properties: Record<string, any> = {}
): boolean => {
  return pushToDataLayer('user_properties', {
    user_id: userId,
    ...properties,
  });
};

/**
 * Initialize debug mode for dataLayer inspection
 * Recommended during development
 */
export const initializeDebugMode = (): void => {
  if (typeof window === 'undefined') return;

  // Make dataLayer inspection available in console
  (window as any).getDataLayerDebug = () => {
    const dataLayer = getDataLayer();
    console.table(dataLayer);
    return dataLayer;
  };

  (window as any).getLastEventDebug = () => {
    const dataLayer = getDataLayer();
    console.log(dataLayer[dataLayer.length - 1]);
    return dataLayer[dataLayer.length - 1];
  };

  console.log(
    '[GTM Debug] Use getDataLayerDebug() or getLastEventDebug() in console to inspect events'
  );
};
